import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { getErrorMessageFromCode, CarrotErrorCodes } from "#/utils/CarrotErrors"
import { useLocation, useHistory } from "react-router-dom"
import queryString from "query-string"
import { useDispatch } from "react-redux"
import { getDeepLinkReturnUrl, redirectToSamlExternalLogin } from "#/utils/RedirectToSamlExternalLogin"
import { useConsumerRoutesEnabled } from "#/services/launchdarkly/useConsumerRoutesEnabled"
import { persistEmail, signIn } from "#/redux/actions/loginActions"
import { useQueryClient } from "@tanstack/react-query"
import {
  attemptBiometricsCredentialsRetrieval,
  shouldAttemptBioAuthenticationLogin,
  showBiometricsEnrollment
} from "carrot-api/MobileNativeClient"
import {
  trackBiometricAuth,
  trackBiometricAuthAttempt,
  trackBiometricAuthEnrollment,
  useBiometricAuthEnrollment
} from "../../context/BiometricAuthEnrollment/BiometricAuthEnrollmentContext"
import { useAuthentication } from "../../context/authentication/AuthenticationProvider"
import { SignIn } from "#/pages/sign-in"
import { defineMessage, MessageDescriptor, useIntl } from "react-intl"
import { Link } from "@carrotfertility/carotene-core"
import { Form } from "@carrotfertility/carotene-core-x"
import { EmployeeSupportUrl } from "#/utils/EmployeeSupportLink"
import { carrotClient } from "#/utils/CarrotClient"
import { HttpErrors } from "#/utils/HttpErrors"
import { reportError, reportErrorMessage } from "#/utils/ErrorReporting"
import { PreloginOutcome } from "#/lib/carrot-api/types/Prelogin"
import { getHeap } from "#/utils/heap"

// Handoff into the subscription funnel for the one Consumer return-visit state this
// container recognises: prelogin's ACTIVATION_REQUIRED — signed up but never paid, so
// there is no app to land in. An in-SPA push is required because carrot-app's
// `is-logged-in` answers false for a never-paid Consumer session, so falling through to
// the app root bounces to a login form.
//
// A LAPSED subscription is deliberately NOT handled here: that member has an account
// carrot-app still signs in, so they land in the app and meet the paywall (CONAPP-421).
//
// /preview rather than /checkout: this member has no plan chosen yet, so the plan
// screen is the first step that applies. Either target is subject to the funnel's own
// progress gating, which bounces a cold tab on to its frontier.
const CONSUMER_SUBSCRIPTION_FUNNEL_PATH = "/consumer/payment/preview"
// Where a LAPSED consumer lands instead of the app home (CONAPP-452) — the interim surface
// while the fuller in-app experience is blocked on a backend limitation. Distinct from the
// funnel above: they already bought once, so restarting billing is a page, not a funnel run.
// Must stay in `PAYWALL_ALLOW_LIST` — it is walled content otherwise, and the gate would
// bounce this push straight back to /paywall.
const CONSUMER_BILLING_DETAIL_PATH = "/consumer/billing-detail"

type LoginContainerState = {
  username: string | null
  password: string | null
  submitting: boolean
  showPasswordInput: boolean
  showEmailInput: boolean
  enableEmailInput: boolean
  showSignUp: boolean
  showContactUs: boolean
  shake: boolean
  errorHasLink: boolean
  errorMessage: MessageDescriptor | null
  isErrorPage: boolean
  samlConfig: any
  returnUrl: string | null
  isIdpOnlySso: boolean
  showSsoAlert: boolean
  showSubmitButton: boolean
  isActivationRequired: boolean
}

const defaultState: LoginContainerState = {
  username: null,
  password: null,
  submitting: false,
  showPasswordInput: false,
  showEmailInput: true,
  samlConfig: null,
  shake: false,
  errorHasLink: false,
  errorMessage: null,
  isErrorPage: false,
  returnUrl: null,
  isIdpOnlySso: false,
  enableEmailInput: true,
  showSignUp: true,
  showContactUs: false,
  showSsoAlert: false,
  showSubmitButton: true,
  isActivationRequired: false
}

export type LoginFormData = {
  username: string
  password: string
  setupBiometricAuth: boolean
}

export const MultiFactorAuthRequired = "MultiFactorAuthRequired"

const LoginContainer = (): React.ReactElement => {
  const location = useLocation()
  const history = useHistory()
  const [state, setState] = useState(defaultState)
  const [enrollInBioAuth, setEnrollInBioAuth] = useState(false)
  const passwordFailedRef = useRef(false)
  const dispatch = useDispatch()
  const passwordInputRef = useRef()
  const queryClient = useQueryClient()
  const {
    setBiometricAuthEnrollmentSuccess,
    setBiometricAuthEnrollmentFailure,
    resetBiometricAuthEnrollmentState,
    disableBiometricAuth
  } = useBiometricAuthEnrollment()
  const { isExplicitLogout, resetExplicitLogout } = useAuthentication()
  const intl = useIntl()
  // The SAME gate Root mounts `/consumer/billing-detail` behind. Without it, a member for
  // whom the flag is off would be sent to a path Root does not mount: it falls through to
  // `NonAuthenticatedRoutesContainer`, which answers with the login form — and since this
  // is a full page load, every successful sign-in would repeat the redirect. A login loop.
  const consumerRoutesEnabled = useConsumerRoutesEnabled()

  // The renewal verdict has to OUTLIVE `login`, because the bio-auth enrollment path
  // finishes somewhere else entirely: `login` kicks off enrollment and returns, and
  // `handleSuccessfulBiometricAuth` runs once the member has enrolled. A ref rather than
  // state for the same reason `passwordFailedRef` is one — it is read inside a later
  // callback, never rendered, so a re-render is neither needed nor wanted.
  const renewalRequiredRef = useRef(false)

  /**
   * Hand a lapsed consumer to Billing. Shared by the password path and the bio-auth
   * enrollment path so the two cannot disagree about where such a member lands — the
   * enrollment path used to reach `signIn()` with the verdict thrown away, which sent a
   * lapsed member back around the login/paywall loop.
   *
   * Returns whether it navigated, so the caller knows to stop rather than fall through to
   * `signIn()`.
   *
   * ⛔ A FULL PAGE LOAD. `is-logged-in` answers false for this member (valid cookie, no
   * Employee UserRole), so SIGN_IN would flip redux `loggedIn` true, Root would mount
   * AppContainer for the beat before the probe corrects it, and AppContainer's paywall
   * gate can REDIRECT in that window — landing them on /paywall, which then falls through
   * to the login form once `loggedIn` goes false again. Reloading straight onto the URL
   * makes `fetchReduxData` derive the false verdict once, from scratch, with nothing to
   * race. Same reasoning as `handleExitToApp` in Root.tsx, running the other way.
   *
   * `resetExplicitLogout` first, so the reload is not read as a deliberate sign-out.
   */
  const handOffLapsedConsumerToBilling = (): boolean => {
    if (!renewalRequiredRef.current || !consumerRoutesEnabled) {
      return false
    }
    resetExplicitLogout()
    window.location.assign(CONSUMER_BILLING_DETAIL_PATH)
    return true
  }

  useState(() => {
    async function execute() {
      if (!isExplicitLogout && (await shouldAttemptBioAuthenticationLogin())) {
        setTimeout(async () => {
          trackBiometricAuthAttempt()
          await attemptBiometricsCredentialsRetrieval(bioAuthLogin)
        }, 1000)
      }
    }
    execute()
  })

  useEffect(() => {
    queryClient.clear()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See https://carrotfertility.atlassian.net/wiki/spaces/PE/pages/2050295461/Remove+Build+Warnings#react-hooks%2Fexhaustive-deps
  }, [])

  const onSubmit = async (formData: LoginFormData) => {
    if (formData.password) {
      await login(formData.username, formData.password, formData.setupBiometricAuth)
    } else {
      await preloginLookup(formData.username)
    }
  }

  useEffect(() => {
    const parsedQueryString = queryString.parse(location.search)
    const errorCode = parsedQueryString?.errorCode
    const isErrorPage = Boolean(errorCode)
    const errorMessage = errorCode ? getErrorMessageFromCode(Array.isArray(errorCode) ? errorCode[0] : errorCode) : null
    const returnUrl = (() => {
      if (!parsedQueryString.ReturnUrl) {
        return null
      }
      return Array.isArray(parsedQueryString.ReturnUrl) ? parsedQueryString.ReturnUrl[0] : parsedQueryString.ReturnUrl
    })()

    setState({
      ...state,
      errorMessage,
      // If we have an error message from the URL upon loading this view, that means we want to display only the message and "Sign in" button, so showEmailInput should be false
      showEmailInput: !isErrorPage,
      isErrorPage,
      returnUrl,
      errorHasLink: Boolean(
        errorCode === CarrotErrorCodes.EMPLOYEE_INACTIVE || CarrotErrorCodes.SAML_UNSUCCESSFUL_OPERATION_EXCEPTION
      )
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See https://carrotfertility.atlassian.net/wiki/spaces/PE/pages/2050295461/Remove+Build+Warnings#react-hooks%2Fexhaustive-deps
  }, [])

  useLayoutEffect(() => {
    if (passwordInputRef.current) {
      return (passwordInputRef.current as HTMLInputElement).focus()
    }
  }, [state.showEmailInput])

  // Listen for back button press
  useEffect(() => {
    const unlisten = history.listen((location, action) => {
      if (action === "POP") {
        setState({
          ...state,
          submitting: false,
          showPasswordInput: false,
          errorMessage: null,
          errorHasLink: false,
          showContactUs: false,
          showSignUp: true,
          enableEmailInput: true,
          showSsoAlert: false,
          showSubmitButton: true,
          isActivationRequired: false
        })
      }
    })
    return () => unlisten()
  }, [history, state])

  const preloginLookup = async (email: string) => {
    if (state.isErrorPage) {
      setState({
        ...state,
        isErrorPage: false,
        showEmailInput: true,
        showPasswordInput: false,
        errorMessage: null,
        errorHasLink: false
      })
      return
    }

    setState({
      ...state,
      submitting: true,
      shake: false,
      errorHasLink: false
    })

    let response
    try {
      response = await carrotClient.prelogin(email)
    } catch (error) {
      setState({
        ...state,
        submitting: false,
        errorMessage: defineMessage({ defaultMessage: "Connection problem" }),
        shake: true,
        errorHasLink: false
      })
      return
    }

    switch (response.outcome) {
      case PreloginOutcome.SsoRedirect:
        redirectToSamlExternalLogin(response.sso.idpEntityId, state.returnUrl ?? getDeepLinkReturnUrl())
        return

      case PreloginOutcome.SsoIdpOnly:
        showIdpOnlySsoScreen()
        return

      case PreloginOutcome.Unregistered:
        dispatch(persistEmail(email))
        history.push(`/signup`)
        return

      case PreloginOutcome.ActivationRequired:
        getHeap().track("Prelogin: Activation Required")
        dispatch(persistEmail(email))
        setState({
          ...state,
          submitting: false,
          showPasswordInput: true,
          enableEmailInput: false,
          samlConfig: null,
          errorMessage: null,
          shake: false,
          errorHasLink: false,
          showContactUs: true,
          showSignUp: false,
          showSsoAlert: false,
          isIdpOnlySso: false,
          isActivationRequired: true
        })

        if (passwordInputRef.current) {
          return (passwordInputRef.current as HTMLInputElement).focus()
        }
        return

      case PreloginOutcome.Password:
        // PASSWORD also covers not-found accounts, so keep the Sign Up link visible (enumeration resistance).
        dispatch(persistEmail(email))
        setState({
          ...state,
          submitting: false,
          showPasswordInput: true,
          enableEmailInput: false,
          samlConfig: response.sso
            ? {
                samlIdpEntityId: response.sso.idpEntityId
              }
            : null,
          errorMessage: null,
          shake: false,
          errorHasLink: false,
          showContactUs: true,
          showSignUp: true,
          showSsoAlert: false,
          isIdpOnlySso: false,
          isActivationRequired: false
        })

        if (passwordInputRef.current) {
          return (passwordInputRef.current as HTMLInputElement).focus()
        }
        return

      default:
        // Fail closed rather than falling through to a password prompt — an unknown outcome for a
        // specific-outcome caller (e.g. ACTIVATION_REQUIRED) would otherwise strand them silently.
        reportErrorMessage(`Unknown prelogin outcome: ${(response as { outcome?: string }).outcome ?? "undefined"}`)
        setState({
          ...state,
          submitting: false,
          errorMessage: defineMessage({
            defaultMessage:
              "Oops, something went wrong. Please try again later or <link>contact us</link> for assistance."
          }),
          shake: true,
          errorHasLink: true
        })
        return
    }
  }

  const clearErrorCodeFromUrl = () => {
    const params = new URLSearchParams(location.search)
    if (params.has("errorCode")) {
      params.delete("errorCode")
      history.replace({ ...location, search: params.toString() })
    }
  }

  /**
   * Persists the locale the user selected before logging in.
   *
   * Before authentication, the user may choose a display language on the login page.
   * That selection is stored in sessionStorage under the key "unauthPreferredLocale"
   * (written by UnauthPreferredLocale.tsx). Because sessionStorage is not tied to the
   * user's account, we must write it to their profile immediately after they authenticate.
   *
   * This function reads and clears the sessionStorage value, then calls updateUser to save
   * it server-side. Errors are caught and reported without interrupting the login flow so
   * that a failed locale update never blocks the user from signing in.
   *
   * After authentication, LocaleService on the backend determines whether the selected
   * language is available for the user's account.
   */
  const updatePreferredLocale = async () => {
    // eslint-disable-next-line no-restricted-syntax
    const preferredLocale = sessionStorage.getItem("unauthPreferredLocale")
    sessionStorage.removeItem("unauthPreferredLocale")
    // eslint-disable-next-line no-restricted-syntax
    if (preferredLocale) {
      try {
        // eslint-disable-next-line no-restricted-syntax -- We update the user's preferredLocale and Carrot-app's LocaleService will then determine if they can use the selected language once signed in.
        await carrotClient.updateUser({ preferredLocale })
      } catch (error) {
        reportError(error)
      }
    }
  }

  const login = async (email: string, password: string, registerBioAuthentication = false) => {
    setState({
      ...state,
      submitting: true,
      shake: false
    })

    try {
      const loginResponse = await carrotClient.login(email, password)

      // The server decides whether MFA is required and signals it in the response; the client just reacts.
      if (loginResponse?.message === MultiFactorAuthRequired) {
        history.push(`/login/enter-code?email=${encodeURIComponent(email)}`)
        return
      }

      // Recorded on the ref, not a local: the bio-auth enrollment branch below returns
      // before any navigation, and the hand-off then happens in
      // `handleSuccessfulBiometricAuth`.
      renewalRequiredRef.current = loginResponse?.status === "renewal_required"
    } catch (error) {
      // A deactivated account is revealed here (403) — only after a correct password — rather than at email entry.
      const isDeactivated = error.name === HttpErrors.FORBIDDEN
      const isWrongPassword = error.name === HttpErrors.BAD_REQUEST

      setState({
        ...state,
        submitting: false,
        errorMessage: isDeactivated
          ? defineMessage({
              defaultMessage:
                "It looks like your account has recently been deactivated. If you need to submit receipts for service prior to leaving your benefit sponsor, please <link>contact us</link>. If you feel that your account has been deactivated in error, please reach out to your internal benefits team for next steps."
            })
          : isWrongPassword
          ? defineMessage({ defaultMessage: "Password does not match." })
          : defineMessage({
              defaultMessage:
                "Oops, something went wrong. Please try again later or <link>contact us</link> for assistance."
            }),
        shake: true,
        errorHasLink: !isWrongPassword
      })

      passwordFailedRef.current = true
      return
    }

    if (registerBioAuthentication) {
      tryBioAuthEnrollment(email, password)
    } else {
      setState({ ...state, submitting: false })
      clearErrorCodeFromUrl()
      await updatePreferredLocale()
      // Activation wins over renewal when both somehow read true: a member who has never paid
      // has nothing to restart, so the funnel is the only destination that can help them.
      if (state.isActivationRequired) {
        // No dispatch(signIn()) for a never-paid Consumer: carrot-app's `is-logged-in` answers
        // false for them (no Employee UserRole yet), so `fetchReduxData` inevitably lands on
        // FETCH_IS_LOGGED_IN_FAILURE — and its FETCH_IS_LOGGED_IN_REQUEST first flips
        // `loadingIsLoggedIn`, which Root's `showPage` gate reads as "unmount everything."
        // The funnel doesn't need redux: `/consumer/payment` is public above the auth split,
        // and identity comes from the BFF's `subscription.getHostSession`. Mirrors
        // `handleConsumerLogin` in Root.tsx.
        history.push(CONSUMER_SUBSCRIPTION_FUNNEL_PATH)
        resetExplicitLogout()
        return
      } else if (handOffLapsedConsumerToBilling()) {
        // A lapsed consumer goes to Billing, which Root renders with NO global layout.
        //
        // INTERIM, and reversing CONAPP-421/422 for a reason worth not re-litigating: the
        // fuller in-app experience for a lapsed member is blocked on a backend limitation,
        // so that standalone page is what they get for now — enough to restart billing or
        // pull receipts for payments they already made. When the backend catches up this
        // destination is expected to change; it is not a settled bit of product.
        return
      }
      dispatch(signIn())
      resetExplicitLogout()
    }
  }

  const bioAuthEnrollmentSuccess = () => {
    trackBiometricAuthEnrollment(true)
    setBiometricAuthEnrollmentSuccess()
    clearErrorCodeFromUrl()
    setState({ ...state, submitting: false })
  }

  const bioAuthEnrollmentFailure = () => {
    trackBiometricAuthEnrollment(false)
    setBiometricAuthEnrollmentFailure()
    // We've logged in successfully, however, for some reason biometric enrollment failed
    setState({ ...state, submitting: false })
  }

  const tryBioAuthEnrollment = (email: string, password: string) => {
    showBiometricsEnrollment(
      email,
      password,
      () => {
        // Timeout is for visual purposes to slow the enrollment process down a touch. On iOS the app logs in before
        // the face id screen completes, which seems off
        setTimeout(() => {
          bioAuthEnrollmentSuccess()
        }, 1000)
      },
      bioAuthEnrollmentFailure
    )
  }

  const bioAuthLogin = async (email: string, password: string): Promise<void> => {
    await login(email, password, false)
    if (passwordFailedRef.current) {
      setState({
        ...state,
        submitting: false,
        shake: false,
        showPasswordInput: false,
        errorMessage: defineMessage({
          defaultMessage: "Biometric authentication failed, please type your credentials again."
        })
      })
      trackBiometricAuth(false)
      passwordFailedRef.current = false
      // This is for change in password failing bioauth
      disableBiometricAuth()
      setEnrollInBioAuth(true)
    } else {
      trackBiometricAuth(true)
    }
  }

  const formattedErrorMessage: React.ReactNode = (() => {
    if (!state.errorMessage) {
      return null
    }

    if (state.errorHasLink) {
      return intl.formatMessage(state.errorMessage, {
        link: (linkContent) => (
          <Link style={{ color: "inherit" }} href={EmployeeSupportUrl} target="_blank">
            {linkContent}
          </Link>
        ),
        supportEmailLink: (linkContent) => (
          <Link style={{ color: "inherit" }} href="mailto:support@get-carrot.com">
            {linkContent}
          </Link>
        )
      })
    }

    return intl.formatMessage(state.errorMessage)
  })()

  const showIdpOnlySsoScreen = () => {
    setState({
      ...state,
      submitting: false,
      showPasswordInput: false,
      samlConfig: null,
      errorMessage: null,
      shake: false,
      errorHasLink: false,
      isIdpOnlySso: true,
      enableEmailInput: false,
      showContactUs: true,
      showSignUp: false,
      showSsoAlert: true,
      showSubmitButton: false
    })
  }

  const handleSuccessfulBiometricAuth = async () => {
    await updatePreferredLocale()
    // Same precedence as the password path: activation wins over renewal, because a member
    // who has never paid has nothing to restart. Both branches return; only the plain
    // PASSWORD sign-in falls through to signIn().
    if (state.isActivationRequired) {
      // See the matching branch in `login()` above for why signIn() is skipped here.
      history.push(CONSUMER_SUBSCRIPTION_FUNNEL_PATH)
      resetExplicitLogout()
      resetBiometricAuthEnrollmentState()
      return
    } else if (handOffLapsedConsumerToBilling()) {
      resetBiometricAuthEnrollmentState()
      return
    }
    dispatch(signIn())
    resetBiometricAuthEnrollmentState()
  }

  return (
    <Form onSubmit={onSubmit}>
      <SignIn
        onSubmit={onSubmit}
        errorMessage={formattedErrorMessage}
        passwordInputRef={passwordInputRef}
        shake={state.shake}
        showEmailInput={state.showEmailInput}
        showPasswordInput={state.showPasswordInput}
        submitting={state.submitting}
        successfulBiometricAuthAction={handleSuccessfulBiometricAuth}
        bioAuthLogin={bioAuthLogin}
        enrollInBioAuth={enrollInBioAuth}
        samlConfig={state.samlConfig}
        redirectToSamlExternalLogin={redirectToSamlExternalLogin}
        returnUrl={state.returnUrl}
        isIdpOnlySso={state.isIdpOnlySso}
        enableEmailInput={state.enableEmailInput}
        showContactUs={state.showContactUs}
        showSignUp={state.showSignUp}
        showSsoAlert={state.showSsoAlert}
        showIdpOnlySsoScreen={showIdpOnlySsoScreen}
        showSubmitButton={state.showSubmitButton}
        isActivationRequired={state.isActivationRequired}
      />
    </Form>
  )
}

export default LoginContainer
